import * as React from 'react';
import { flushSync } from 'react-dom';
import { styled } from '@linaria/react';

import Demo from '@/components/Demo';
import SliderControl from '@/components/Controls/SliderControl';
import ArrowShowingDistance from '@/components/ArrowShowingDistance';

interface Props {}

const SENTENCE =
  'Hello world! This is a sentence that includes several different words.';

const MAX_NUM_OF_CHARS = SENTENCE.length;

const THRESHOLD_NUM_OF_CHARS = 19;

function RecursiveNightmare({}: Props) {
  const ref = React.useRef<HTMLDivElement>(null);

  const [numOfCharacters, setNumOfCharacters] =
    React.useState(MAX_NUM_OF_CHARS);
  const [arrowWidth, setArrowWidth] = React.useState<number>(0);
  const [applyingQuery, setApplyingQuery] =
    React.useState<boolean>(false);

  React.useEffect(() => {
    if (!ref.current) {
      return;
    }

    const boundingBox = ref.current.getBoundingClientRect();

    setArrowWidth(boundingBox.width);

    if (numOfCharacters <= THRESHOLD_NUM_OF_CHARS) {
      window.setTimeout(() => {
        flushSync(() => {
          setApplyingQuery(!applyingQuery);
        });
      }, 500);
    }
  }, [numOfCharacters, applyingQuery]);

  return (
    <Demo
      controlPosition="top"
      initialValues={{}}
      customGrid={{
        gridTemplateColumns: `min(500px, 100%) !important`,
        justifyContent: 'center',
      }}
      renderAltControls={() => (
        <SliderWrapper>
          <SliderControl
            id="numOfCharacters"
            key="numOfCharacters"
            name="numOfCharacters"
            label="Num of Characters"
            value={numOfCharacters}
            setValue={setNumOfCharacters}
            min={11}
            max={MAX_NUM_OF_CHARS}
            handleColor={
              numOfCharacters <= THRESHOLD_NUM_OF_CHARS
                ? 'var(--color-secondary)'
                : undefined
            }
          />
        </SliderWrapper>
      )}
    >
      {() => {
        const boldCharacters = SENTENCE.slice(
          0,
          Math.min(12, numOfCharacters)
        );
        const remainingCharacters = SENTENCE.slice(
          12,
          Math.max(12, numOfCharacters)
        );

        const fontSize =
          numOfCharacters <= THRESHOLD_NUM_OF_CHARS && applyingQuery
            ? 3
            : 1.125;

        return (
          <Wrapper>
            {arrowWidth > 5 && (
              <ArrowWrapper>
                <ArrowShowingDistance
                  animateTransitions="label-only"
                  axis="horizontal"
                  unit="rem"
                  distance={arrowWidth + 4}
                  flipTextSide
                />
              </ArrowWrapper>
            )}
            <Sentence ref={ref}>
              <strong style={{ fontSize: `${fontSize}rem` }}>
                {boldCharacters}
                {boldCharacters[boldCharacters.length - 1] === ' ' &&
                  '​'}
              </strong>
              {remainingCharacters}
              {remainingCharacters[remainingCharacters.length - 1] ===
                ' ' && '​'}
            </Sentence>
          </Wrapper>
        );
      }}
    </Demo>
  );
}

const SliderWrapper = styled.div`
  width: 360px;
  max-width: 100%;
  margin-inline: auto;
`;

const Wrapper = styled.div`
  position: relative;
`;

const Sentence = styled.div`
  display: block;
  width: fit-content;
  outline: solid var(--color-secondary);
  border-radius: 2px;
`;

const ArrowWrapper = styled.div`
  position: absolute;
  transform: translate(-2px, -100%);
  top: -4px;
  left: 0;
  right: 0;
  margin-inline: auto;
`;

export default RecursiveNightmare;
